// src/Auth.js
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
} from '@mui/material';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useNavigate, useLocation } from 'react-router-dom';

const friendlyAuthError = (err) => {
  const code = err?.code || '';
  switch (code) {
    case 'auth/email-already-in-use':
      return 'That email is already in use. Try logging in instead.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/weak-password':
      return 'Password is too weak. Use at least 6 characters.';
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a minute and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // ✅ Open signup automatically on /signup
  const initialIsSignup = useMemo(() => location.pathname === '/signup', [location.pathname]);
  const [isSignup, setIsSignup] = useState(initialIsSignup);

  useEffect(() => {
    setIsSignup(initialIsSignup);
  }, [initialIsSignup]);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Forgot password modal state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState({ type: '', msg: '' });
  const [resetSending, setResetSending] = useState(false);

  const cleanEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const openReset = () => {
    setResetEmail(cleanEmail || '');
    setResetStatus({ type: '', msg: '' });
    setResetOpen(true);
  };

  const handleSendReset = async () => {
    try {
      setResetStatus({ type: '', msg: '' });

      const emailToUse = (resetEmail || '').trim().toLowerCase();
      if (!emailToUse) {
        setResetStatus({ type: 'error', msg: 'Enter your email address first.' });
        return;
      }

      setResetSending(true);
      await sendPasswordResetEmail(auth, emailToUse);

      setResetStatus({
        type: 'success',
        msg: 'Password reset email sent. Check your inbox (and spam) for the link.',
      });
    } catch (err) {
      setResetStatus({
        type: 'error',
        msg: friendlyAuthError(err),
      });
    } finally {
      setResetSending(false);
    }
  };

  /**
   * ✅ Safe: create missing docs + fill missing fields ONLY
   * ❌ Never resets access/plan/subscriptionStatus on login
   */
  const ensureDefaults = async (user) => {
    const uid = user.uid;

    // Root user doc: users/{uid}
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(
        userRef,
        {
          email: user.email || cleanEmail,
          role: 'user',
          plan: 'free',
          subscriptionStatus: 'inactive',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      const data = userSnap.data() || {};
      const patch = { updatedAt: serverTimestamp() };

      if (!data.email) patch.email = user.email || cleanEmail;
      if (!data.role) patch.role = 'user';
      if (!data.createdAt) patch.createdAt = serverTimestamp();

      await setDoc(userRef, patch, { merge: true });
    }

    // Settings singleton: users/{uid}/settings/main
    const settingsRef = doc(db, 'users', uid, 'settings', 'main');
    const settingsSnap = await getDoc(settingsRef);

    if (!settingsSnap.exists()) {
      await setDoc(
        settingsRef,
        {
          nextInvoiceId: 1,
          nextReportId: 1,
          access: 'locked',
          lifetime: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      const data = settingsSnap.data() || {};
      const patch = { updatedAt: serverTimestamp() };

      if (data.nextInvoiceId == null) patch.nextInvoiceId = 1;
      if (data.nextReportId == null) patch.nextReportId = 1;
      if (!data.access) patch.access = 'locked';
      if (data.lifetime == null) patch.lifetime = false;
      if (!data.createdAt) patch.createdAt = serverTimestamp();

      await setDoc(settingsRef, patch, { merge: true });
    }

    // Provider profile defaults: users/{uid}/userInfo/main
    const infoRef = doc(db, 'users', uid, 'userInfo', 'main');
    const infoSnap = await getDoc(infoRef);

    if (!infoSnap.exists()) {
      await setDoc(
        infoRef,
        {
          name: '',
          abn: '',
          ndisProviderNumber: '',
          address: '',
          bankDetails: '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      const data = infoSnap.data() || {};
      const patch = { updatedAt: serverTimestamp() };
      if (!data.createdAt) patch.createdAt = serverTimestamp();
      await setDoc(infoRef, patch, { merge: true });
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (submitting) return;

  setError('');

  const pwd = password || '';
  if (pwd.length < 6) {
    setError('Password must be at least 6 characters');
    return;
  }

  setSubmitting(true);

  try {
    let userCredential;
    let didCreateNewUser = false;

    if (isSignup) {
      try {
        userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, pwd);
        didCreateNewUser = true; // ✅ REAL signup
      } catch (err) {
        if (err?.code === 'auth/email-already-in-use') {
          userCredential = await signInWithEmailAndPassword(auth, cleanEmail, pwd);
          didCreateNewUser = false;
        } else {
          throw err;
        }
      }
    } else {
      userCredential = await signInWithEmailAndPassword(auth, cleanEmail, pwd);
    }

    if (userCredential?.user) {
      await ensureDefaults(userCredential.user);
    }

    // ✅ META EVENT — only for brand-new accounts
    if (didCreateNewUser && window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        em: cleanEmail,
      });
    }

    navigate('/');
  } catch (err) {
    console.error('Auth error:', err);
    setError(friendlyAuthError(err));
  } finally {
    setSubmitting(false);
  }
};


  const title = isSignup ? 'Create your account' : 'Welcome back';
  const subtitle = isSignup
    ? 'Create invoices from shift reports. Automatically.'
    : 'Log in to continue to DashFlow.';

  const primaryButtonText = submitting
    ? isSignup
      ? 'Creating…'
      : 'Logging in…'
    : isSignup
    ? 'Create account'
    : 'Log in';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        // Premium background (subtle)
        background:
          'radial-gradient(1200px circle at 10% 10%, rgba(0,0,0,0.06), transparent 40%), radial-gradient(900px circle at 90% 20%, rgba(0,0,0,0.05), transparent 45%), #f7f7f8',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          p: 4,
          bgcolor: 'white',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
        }}
      >
        {/* Logo placeholder (optional) */}
       <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
  <Typography
    sx={{
      fontFamily: '"Questrial", sans-serif',
      fontSize: 28,
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'black',
      userSelect: 'none',
    }}
  >
    DashFlow
  </Typography>
</Box>


        <Typography variant="h4" textAlign="center" mb={1} color="black" fontWeight={800}>
          {title}
        </Typography>

        <Typography variant="body2" textAlign="center" sx={{ color: 'text.secondary', mb: 2.5 }}>
          {subtitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            required
            autoComplete="email"
          />

          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            required
            autoComplete={isSignup ? 'new-password' : 'current-password'}
          />

          {!isSignup && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Link component="button" type="button" underline="hover" onClick={openReset} sx={{ fontSize: 14 }}>
                Forgot password?
              </Link>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 2.5, py: 1.4, borderRadius: 2 }}
            disabled={submitting}
          >
            {primaryButtonText}
          </Button>

          {/* Small trust line (only if true) */}
          {isSignup && (
            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1.25, color: 'text.secondary' }}>
              Create your account now — you can start a free trial inside the app.
            </Typography>
          )}
        </form>

        <Divider sx={{ my: 2.25 }} />

        {/* Secondary action: swap modes */}
        <Button
          fullWidth
          disabled={submitting}
          onClick={() => {
            setIsSignup((v) => !v);
            setError('');
            // Keep URL consistent with mode (premium)
            navigate(isSignup ? '/login' : '/signup');
          }}
          sx={{ borderRadius: 2 }}
        >
          {isSignup ? 'Already have an account? Log in' : 'New here? Create an account'}
        </Button>

        <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Reset password</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1, color: 'text.secondary' }}>
              Enter your email and we’ll send you a reset link.
            </Typography>

            {resetStatus.msg && (
              <Alert severity={resetStatus.type || 'info'} sx={{ mb: 1 }}>
                {resetStatus.msg}
              </Alert>
            )}

            <TextField
              label="Email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              fullWidth
              autoFocus
              autoComplete="email"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetOpen(false)}>Close</Button>
            <Button variant="contained" onClick={handleSendReset} disabled={resetSending}>
              {resetSending ? 'Sending…' : 'Send reset email'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default Auth;







